import React, { useCallback, useEffect, useState } from 'react'
import { getTranslator, TranslateContextProvider } from 'client/common/translator'
import CrossSellProducts from 'client/common/components/crossSell/CrossSellProducts'
import { Divider } from 'client/common/components/Divider'
import { useAppearedInView } from 'client/common/hooks/useInview'
import { ChakraProvider } from '@chakra-ui/react'
import { chemistryTheme, Container, Box } from 'chemistry'
import { dispatchPageBusEvent } from '../../common/pageBus'
import AddToCartButton from './components/AddToCartButton/AddToCartButton'
import TotalBundlePrice from './components/TotalBundlePrice/TotalBundlePrice'
import { useFetchBundleData } from './hooks/useFetchBundleData'
import { PRODUCT_ADDED_BUS_EVENT_TYPE, PRODUCT_ADDED_FROM_PDP_BUNDLE_PRODUCTS } from '../../pageBusEvents'
import { generateAddBundleToCartHref, updateCheckedProducts } from './utils'

const Component = props => {
  // Reason why use this instead of useRef https://reactjs.org/docs/hooks-faq.html#how-can-i-measure-a-dom-node
  const [crossSellRef, setCrossSellRef] = useState({})
  const [checkedProducts, setCheckedProducts] = useState([])
  const [addBundleToCartHref, setAddBundleToCartHref] = useState('')
  const crossSellRefFn = useCallback(node => {
    if (node !== null) {
      return setCrossSellRef({ current: node })
    }
    return {}
  }, [])

  const inView = useAppearedInView(crossSellRef)

  const { data, error, loading, onProductClick } = useFetchBundleData({ ...props }, inView)
  const { translations } = props

  useEffect(() => {
    dispatchPageBusEvent({
      type: 'BUNDLE_FRAGMENT_RENDERED'
    })
  }, [])

  useEffect(() => {
    setCheckedProducts((data?.products?.collection || []).map(p => p.id))
    setAddBundleToCartHref(generateAddBundleToCartHref(data?.products?.collection))
  }, [data?.products?.collection])

  const t = getTranslator(translations)
  const titleKey = t('lister.bundle.title')
  const rootClassName = ''
  const onProductCheck = useCallback(
    selectedProductId => {
      const productIds = updateCheckedProducts(checkedProducts, selectedProductId)
      setCheckedProducts(productIds)
      setAddBundleToCartHref(generateAddBundleToCartHref(data?.products?.collection, productIds))
    },
    [checkedProducts, data?.products?.collection]
  )

  const onCartClick = () => {
    const collection = data?.products?.collection
    if (!Array.isArray(collection)) return
    dispatchPageBusEvent({
      type: PRODUCT_ADDED_BUS_EVENT_TYPE,
      addToCartLocation: PRODUCT_ADDED_FROM_PDP_BUNDLE_PRODUCTS,
      quantityPerProduct: 1,
      productsCollection: collection
    })
  }

  return (
    <ChakraProvider resetCSS={false} theme={chemistryTheme}>
      <TranslateContextProvider translations={translations}>
        <Container marginInline={0} wrapper={{ padding: 0 }}>
          <Box className='bundle-container'>
            <CrossSellProducts
              {...{
                ...props,
                data,
                error,
                loading,
                titleKey,
                rootClassName,
                crossSellRef: crossSellRefFn,
                checkedProducts,
                onProductClick,
                onProductCheck,
                target: '_blank',
                pageType: 'productDetail',
                inView
              }}
            />
            {!!checkedProducts.length && (
              <Box className='flex items-center justify-end'>
                <TotalBundlePrice products={data?.products?.collection.filter(p => checkedProducts.includes(p.id))} />
                <AddToCartButton onCartClick={onCartClick} addBundleToCartHref={addBundleToCartHref} />
              </Box>
            )}
            <Divider />
          </Box>
        </Container>
      </TranslateContextProvider>
    </ChakraProvider>
  )
}

export default { Component }
